import { AdvancedTaskQuestionMode } from "../types/dtos/forms/AdvancedTaskQuestionMode";
import TaskManagementConfigDto from "../types/dtos/forms/TaskManagementConfigDto";
import { FormType } from "../types/forms/FormType";

/** The subset of the task management config needed to spot a dual-prep journey. */
export type DualPrepJourneyConfig = Pick<
  TaskManagementConfigDto,
  "isDualPrepJourney"
>;

/** The subset of the task management config needed to spot a dual-prep check-in question. */
export type DualPrepCheckInConfig = DualPrepJourneyConfig &
  Pick<TaskManagementConfigDto, "questionMode">;

/** Check-in modes are the only ones used for the planning half of a dual-prep pairing. */
const CHECK_IN_MODES: AdvancedTaskQuestionMode[] = [
  "TASK-CHECK-IN-ONLY",
  "TASK-CHECK-IN-AND-CREATION",
];

/**
 * Whether the form being answered is the planning (prep) half of a dual-prep pairing rather than
 * the meeting document. Journeys are only ever used for planning, so the prep flag is only
 * meaningful for collab docs, where the same component renders both phases.
 */
export function isDualPrepPlanningPhase(
  formType: FormType,
  isInPrepMode?: boolean | null
): boolean {
  if (formType === "JOURNEY") return true;
  return isInPrepMode === true;
}

/**
 * Dual-prep planning check-ins let people keep working on their tasks ahead of the meeting, so
 * chasing them for enforced comments or overdue dates belongs on the meeting document instead.
 */
export function isDualPrepPlanningCheckIn(
  config: DualPrepCheckInConfig | null | undefined,
  formType: FormType,
  isInPrepMode?: boolean | null
): boolean {
  if (!config || config.isDualPrepJourney !== true) return false;
  if (!CHECK_IN_MODES.includes(config.questionMode)) return false;
  return isDualPrepPlanningPhase(formType, isInPrepMode);
}

/**
 * Whether this question sits on the planning half of a dual-prep pairing. Unlike
 * isDualPrepPlanningCheckIn this is true for every question mode, so rating and review-form
 * checks can be deferred for creation-only questions as well as check-ins.
 */
export function isDualPrepPlanningForm(
  config: DualPrepJourneyConfig | null | undefined,
  formType: FormType,
  isInPrepMode?: boolean | null
): boolean {
  if (!config || config.isDualPrepJourney !== true) return false;
  return isDualPrepPlanningPhase(formType, isInPrepMode);
}
